import { z } from 'zod';
import { addDaysLocal, formatDuration, type LocalDate, todayLocal } from '../time';
import {
  amountSchema,
  currencySchema,
  idSchema,
  localDateSchema,
  markdownSchema,
  nameSchema,
  versionSchema,
} from './common';

/**
 * Usługi (docs/3VTOS_MVP.md §5, docs/MODULE_UTRZYMANIE.md §2 „Umowa utrzymania”): `services`
 * (packages/db/src/schema/crm.ts). Usługa zawsze ma klienta. Umowa utrzymania = usługa `kind: 'maintenance'`
 * z ceną, cyklem i następną płatnością (przypomnienia: reguły w module Utrzymanie).
 * Moduł uprawnień: `projects` (lista „Usługi” w Projektach) i `maintenance` (umowy w Utrzymaniu).
 */

export const SERVICE_KINDS = ['maintenance', 'consulting', 'hours_package', 'other'] as const;
export type ServiceKind = (typeof SERVICE_KINDS)[number];
export const serviceKindSchema = z.enum(SERVICE_KINDS);

export const SERVICE_STATUSES = ['active', 'paused', 'ended'] as const;
export type ServiceStatus = (typeof SERVICE_STATUSES)[number];
export const serviceStatusSchema = z.enum(SERVICE_STATUSES);

/** Cykl rozliczenia (usługi, koszty dostawców). `none` = jednorazowo / bez cyklu. */
export const BILLING_CYCLES = ['year', 'month', 'quarter', 'other', 'none'] as const;
export type BillingCycle = (typeof BILLING_CYCLES)[number];
export const billingCycleSchema = z.enum(BILLING_CYCLES);

/** Długość cyklu w miesiącach (przesunięcie następnej płatności); `other`/`none` = ręcznie. */
export const BILLING_CYCLE_MONTHS: Record<BillingCycle, number | null> = {
  year: 12,
  quarter: 3,
  month: 1,
  other: null,
  none: null,
};

const serviceFieldsBase = z.object({
  clientId: idSchema,
  name: nameSchema,
  kind: serviceKindSchema,
  descriptionMd: markdownSchema.nullable().optional(),
  periodStart: localDateSchema.nullable().optional(),
  periodEnd: localDateSchema.nullable().optional(),
  /** warunki rozliczenia, np. „płatne z góry, faktura 7 dni” */
  billingTerms: z.string().trim().max(2000).nullable().optional(),
  /** cena netto za cykl; wymaga flagi „Widzi stawki i kwoty” */
  price: amountSchema.nullable().optional(),
  currency: currencySchema.default('PLN'),
  cycle: billingCycleSchema.default('none'),
  nextPaymentDate: localDateSchema.nullable().optional(),
  /** pula godzin (pakiet, utrzymanie) w sekundach */
  hoursPoolSeconds: z.number().int().positive().nullable().optional(),
  status: serviceStatusSchema.default('active'),
  /** przypomnienie do klienta wysyłane automatycznie z zatwierdzonego szablonu (domyślnie przez Akceptacje) */
  autoClientReminder: z.boolean().default(false),
  sourceKnowledgeId: idSchema.nullable().optional(),
});

export type ServiceFields = z.infer<typeof serviceFieldsBase>;

/** Spójność: koniec okresu nie przed początkiem (CHECK w bazie). */
export function serviceFieldsRefinement(
  v: Partial<Pick<ServiceFields, 'periodStart' | 'periodEnd'>>,
  ctx: z.RefinementCtx,
): void {
  if (v.periodStart && v.periodEnd && v.periodEnd < v.periodStart) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['periodEnd'], message: 'Koniec przed początkiem' });
  }
}

/** Pola usługi bez reguł spójności (do `.extend()`, `.partial()`, `.pick()`). */
export const serviceFieldsShape = serviceFieldsBase;
export const serviceFieldsSchema = serviceFieldsBase.superRefine(serviceFieldsRefinement);

// ── Cykl i daty (Europe/Warsaw, daty biznesowe "YYYY-MM-DD") ────────────────

/** Liczba cykli w roku (kwota „/ rok” na liście Utrzymania); `none` = jednorazowo (bez kwoty rocznej). */
export const CYCLES_PER_YEAR: Record<BillingCycle, number | null> = {
  year: 1,
  quarter: 4,
  month: 12,
  other: 1,
  none: null,
};

/** Dzień miesiąca daty biznesowej („2027-01-31” → 31): kotwica cyklu płatności i odnowień. */
export function anchorDayOf(date: string): number {
  return Number(date.slice(8, 10));
}

/**
 * Dodaje miesiące do daty biznesowej. Dzień miesiąca przycinany do ostatniego dnia (31.01 + 1 mies. = 28/29.02).
 * `anchorDay` = dzień, od którego cykl się zaczął (kotwica): przycięcie dotyczy tylko wyniku, więc kolejne kroki
 * wracają do kotwicy (31.01 → 28.02 → 31.03 → 30.04; 29.02.2028 → 28.02.2029 … → 29.02.2032). Bez kotwicy liczy
 * od dnia `date`.
 * Przykład: addMonthsLocal('2026-10-16', 12) = '2027-10-16'.
 */
export function addMonthsLocal(date: string, months: number, anchorDay?: number | null): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const total = y * 12 + (m - 1) + months;
  const ny = Math.floor(total / 12);
  const nm = total - ny * 12;
  const lastDay = new Date(Date.UTC(ny, nm + 1, 0)).getUTCDate();
  const nd = Math.min(anchorDay ?? d, lastDay);
  return `${String(ny).padStart(4, '0')}-${String(nm + 1).padStart(2, '0')}-${String(nd).padStart(2, '0')}`;
}

/**
 * Następny termin po `date` w danym cyklu (`null` dla `other`/`none`: ustawiany ręcznie). `anchorDay`: kotwica
 * cyklu (`services.anchor_day`, `supplier_renewals.anchor_day`), żeby koniec miesiąca nie „dryfował”.
 */
export function nextCycleDate(date: string, cycle: BillingCycle, anchorDay?: number | null): string | null {
  const months = BILLING_CYCLE_MONTHS[cycle];
  return months === null ? null : addMonthsLocal(date, months, anchorDay);
}

// ── Wejścia usług (services.*) ──────────────────────────────────────────────

/** Domyślne przypomnienia nowej umowy utrzymania (MODULE_UTRZYMANIE §3; Figma „Dodaj zasób”). */
export const serviceReminderOptionsSchema = z.object({
  /** do zespołu (opiekun klienta, owner/admin): 30 i 7 dni przed */
  owner: z.boolean().default(true),
  /** do klienta: 30 dni przed, mail przez Akceptacje */
  client: z.boolean().default(true),
  /** po terminie: co 7 dni, najwyżej 3 razy */
  overdue: z.boolean().default(true),
});
export type ServiceReminderOptions = z.infer<typeof serviceReminderOptionsSchema>;

export const serviceListInput = z
  .object({
    clientId: idSchema.optional(),
    kind: serviceKindSchema.optional(),
    status: serviceStatusSchema.optional(),
    q: z.string().trim().max(200).optional(),
    includeArchived: z.boolean().default(false),
  })
  .default({});
export type ServiceListInput = z.input<typeof serviceListInput>;

export const serviceByClientInput = z.object({
  clientId: idSchema,
  includeArchived: z.boolean().default(false),
});

export const serviceIdInput = z.object({ id: idSchema });

/** Objęte zasoby (hosted_assets.service_id), najwyżej 50; zasoby muszą należeć do klienta usługi. */
const serviceAssetIdsSchema = z.array(idSchema).max(50);

export const createServiceInput = serviceFieldsShape
  .extend({
    assetIds: serviceAssetIdsSchema.optional(),
    /** tylko umowa utrzymania (`kind: 'maintenance'`); domyślnie wszystkie trzy reguły */
    reminders: serviceReminderOptionsSchema.optional(),
  })
  .superRefine(serviceFieldsRefinement);
export type CreateServiceInput = z.input<typeof createServiceInput>;

/** Edycja: bez zmiany klienta (zasoby i zadania usługi należą do klienta). */
export const updateServiceInput = serviceFieldsShape
  .omit({ clientId: true })
  .partial()
  .extend({ id: idSchema, version: versionSchema, assetIds: serviceAssetIdsSchema.optional() })
  .superRefine(serviceFieldsRefinement);
export type UpdateServiceInput = z.input<typeof updateServiceInput>;

export const archiveServiceInput = z.object({
  id: idSchema,
  version: versionSchema,
  /** false = przywróć z archiwum */
  archived: z.boolean().default(true),
});

// ── Wyjścia ─────────────────────────────────────────────────────────────────

export interface ServiceClientRef {
  id: string;
  name: string;
  color: string | null;
}

export interface ServiceAssetRef {
  id: string;
  name: string;
  type: string;
  status: string;
}

/**
 * Usługa. Bez flagi „Widzi stawki i kwoty” pole `price` jest pominięte (nie `null`), ARCHITECTURE §5.
 * `daysToPayment`: dni do następnej płatności w Europe/Warsaw (ujemne = po terminie), `null` bez daty.
 */
export interface ServiceDto {
  id: string;
  version: number;
  clientId: string;
  client: ServiceClientRef;
  name: string;
  kind: ServiceKind;
  descriptionMd: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  billingTerms: string | null;
  price?: string | null;
  currency: string;
  cycle: BillingCycle;
  nextPaymentDate: string | null;
  daysToPayment: number | null;
  hoursPoolSeconds: number | null;
  status: ServiceStatus;
  autoClientReminder: boolean;
  sourceKnowledgeId: string | null;
  assets: ServiceAssetRef[];
  /**
   * Wykorzystanie puli godzin w bieżącym miesiącu (Europe/Warsaw) — tylko aktywna usługa z pulą
   * (`isActivePoolService`), inaczej `null`. Same godziny, bez kwot (widoczne także bez „Widzi stawki i kwoty”).
   */
  pool?: ServicePoolUsage | null;
  archivedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

// ── Pula godzin (DECISIONS 2026-10-17 [P] „Zgłoszenia z utrzymania liczą się do puli godzin”) ──────────────

/**
 * Pula = godziny na miesiąc kalendarzowy (Europe/Warsaw) aktywnej usługi klienta z pulą. Wykorzystanie = suma czasu
 * wpisów z tą usługą (wszystkie zadania usługi i wpisy bez zadania, wszystkie osoby, bez usuniętych), liczone po
 * `started_at` w strefie Warszawy. Same godziny — bez kwot.
 */
export interface ServicePoolUsage {
  serviceId: string;
  serviceName: string;
  serviceKind: ServiceKind;
  clientId: string;
  /** miesiąc kalendarzowy „YYYY-MM” (Europe/Warsaw) */
  month: string;
  /** pierwszy i ostatni dzień miesiąca (daty lokalne) */
  from: string;
  to: string;
  usedSeconds: number;
  poolSeconds: number;
  /** ile zostało (0, gdy pula wykorzystana) */
  remainingSeconds: number;
  /** ile ponad pulę (0, gdy w puli) */
  overSeconds: number;
}

/** Bursztyn od 80% puli, czerwień ponad 100% (jak budżet godzin projektu). */
export const POOL_WARNING_RATIO = 0.8;

export const poolMonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Miesiąc w formacie RRRR-MM');

/** Wykorzystanie pul: klienta (aktywne usługi z pulą) albo wskazanych usług (karty zadań). */
export const servicePoolsInput = z
  .object({
    clientId: idSchema.optional(),
    serviceIds: z.array(idSchema).max(200).optional(),
    /** domyślnie bieżący miesiąc (Europe/Warsaw) */
    month: poolMonthSchema.optional(),
  })
  .refine((v) => v.clientId !== undefined || v.serviceIds !== undefined, {
    message: 'Podaj klienta albo usługi',
    path: ['clientId'],
  });
export type ServicePoolsInput = z.input<typeof servicePoolsInput>;

/** Miesiąc „YYYY-MM” daty lokalnej albo chwili (Europe/Warsaw). */
export function poolMonthOf(value: LocalDate | Date): string {
  const date = typeof value === 'string' ? value : todayLocal(value);
  return date.slice(0, 7);
}

/** Zakres miesiąca: pierwszy dzień, ostatni dzień i pierwszy dzień następnego (daty lokalne). */
export function poolMonthRange(month: string): { from: LocalDate; to: LocalDate; nextFrom: LocalDate } {
  const from = `${month}-01`;
  const nextFrom = addMonthsLocal(from, 1);
  return { from, to: addDaysLocal(nextFrom, -1), nextFrom };
}

/**
 * Aktywna usługa z pulą: pula > 0, status „Aktywna”, nie w archiwum, `today` w okresie usługi (brak daty = bez
 * ograniczenia). Tylko taka usługa ma wykorzystanie puli, trafia do nowych zgłoszeń z portalu i na kartę klienta.
 */
export function isActivePoolService(
  s: {
    hoursPoolSeconds: number | null;
    status: string;
    archivedAt: Date | null;
    periodStart: string | null;
    periodEnd: string | null;
  },
  today: LocalDate,
): boolean {
  return (
    (s.hoursPoolSeconds ?? 0) > 0 &&
    s.status === 'active' &&
    s.archivedAt === null &&
    (s.periodStart === null || s.periodStart <= today) &&
    (s.periodEnd === null || s.periodEnd >= today)
  );
}

/** Pozostało / ponad pulę z wykorzystania. */
export function poolBalance(
  usedSeconds: number,
  poolSeconds: number,
): { remainingSeconds: number; overSeconds: number } {
  return {
    remainingSeconds: Math.max(0, poolSeconds - usedSeconds),
    overSeconds: Math.max(0, usedSeconds - poolSeconds),
  };
}

export type PoolTone = 'neutral' | 'warning' | 'danger';

/** Kolor puli: czerwień ponad 100%, bursztyn od 80%, inaczej neutralny. */
export function poolTone(u: Pick<ServicePoolUsage, 'usedSeconds' | 'poolSeconds'>): PoolTone {
  if (u.poolSeconds <= 0) return 'neutral';
  const ratio = u.usedSeconds / u.poolSeconds;
  if (ratio > 1) return 'danger';
  return ratio >= POOL_WARNING_RATIO ? 'warning' : 'neutral';
}

/** Pula wykorzystana w całości (wykorzystanie ≥ pula). */
export function poolExhausted(u: Pick<ServicePoolUsage, 'usedSeconds' | 'poolSeconds'>): boolean {
  return u.poolSeconds > 0 && u.usedSeconds >= u.poolSeconds;
}

const POOL_MONTHS = [
  'styczeń',
  'luty',
  'marzec',
  'kwiecień',
  'maj',
  'czerwiec',
  'lipiec',
  'sierpień',
  'wrzesień',
  'październik',
  'listopad',
  'grudzień',
] as const;

/** „2026-10” → „październik”. */
export function poolMonthName(month: string): string {
  return POOL_MONTHS[Number(month.slice(5, 7)) - 1] ?? month;
}

/** „1 h 20 min z 3 h” (sam stan, bez miesiąca). */
export function poolUsedText(u: Pick<ServicePoolUsage, 'usedSeconds' | 'poolSeconds'>): string {
  return `${formatDuration(u.usedSeconds)} z ${formatDuration(u.poolSeconds)}`;
}

/** Karta klienta: „1 h 20 min z 3 h · październik”. */
export function poolUsageLine(u: Pick<ServicePoolUsage, 'usedSeconds' | 'poolSeconds' | 'month'>): string {
  return `${poolUsedText(u)} · ${poolMonthName(u.month)}`;
}

/** Notatka pod paskiem: „zostało 1 h 40 min” / „pula wykorzystana” / „ponad pulę: 20 min”. */
export function poolBalanceText(u: Pick<ServicePoolUsage, 'remainingSeconds' | 'overSeconds'>): string {
  if (u.overSeconds > 0) return `ponad pulę: ${formatDuration(u.overSeconds)}`;
  if (u.remainingSeconds === 0) return 'pula wykorzystana';
  return `zostało ${formatDuration(u.remainingSeconds)}`;
}

/** Chip na karcie zadania: „Pula: zostało 1 h 40 min” / „Pula wykorzystana” / „Ponad pulę: 20 min”. */
export function poolChipText(u: Pick<ServicePoolUsage, 'remainingSeconds' | 'overSeconds'>): string {
  if (u.overSeconds > 0) return `Ponad pulę: ${formatDuration(u.overSeconds)}`;
  if (u.remainingSeconds === 0) return 'Pula wykorzystana';
  return `Pula: zostało ${formatDuration(u.remainingSeconds)}`;
}

/** Dopisek w Inboxie nowego zgłoszenia: „Pula na październik wykorzystana (3 h 20 min z 3 h)”. */
export function poolExhaustedNote(u: Pick<ServicePoolUsage, 'usedSeconds' | 'poolSeconds' | 'month'>): string {
  return `Pula na ${poolMonthName(u.month)} wykorzystana (${poolUsedText(u)})`;
}
